import {Injectable, Injector} from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import {delay, Observable, of, throwError} from 'rxjs'

import {catchError, switchMap} from 'rxjs/operators'
import {environment} from '../../../environments/environment'
import {LocaleService} from "../locale.service";
import {UserSessionService} from "../user-session.service";
import {RedirectUrlService} from "../redirect-url.service";
import {Router} from "@angular/router";
import {formatDistanceToNow, fromUnixTime} from "date-fns";
import {ToastrService} from "ngx-toastr";
import {Backend} from "@generated-backend-types";
import {TranslateService} from "@ngx-translate/core";
import PublicErrorCodeEnum = Backend.App.Enums.PublicErrorCodeEnum;

/**
 * This interceptor adds the correct API endpoint to each request.
 */
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly http: HttpClient
  private readonly localeService: LocaleService
  private readonly redirectUrlService: RedirectUrlService
  private readonly router: Router
  private toastr: ToastrService;
  private translateService: TranslateService;

  private delayDueToRateLimitUntil: Date = new Date();

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // do not modify requests to frontend server
    if (req.url.includes(`${window.location.protocol}//${window.location.host}`)) {
      return next.handle(req)
    }

    // do not modify requests for translation files
    if (req.url.includes('i18n')) {
      return next.handle(req)
    }

    // do not modify requests for other domains
    if (req.url.startsWith('http') && !req.url.startsWith(environment.api.url)) {
      return next.handle(req)
    } else if (!req.url.startsWith('http')) {
      req = req.clone({
        url: environment.api.url + req.url
      })
    }

    req = req.clone({
      withCredentials: true,
      headers: req.headers.set('accept-language', this.localeService.currentLocale)
    })

    // fetch csrf token if cookie not present!
    if (!document.cookie.includes('XSRF-TOKEN') && !req.url.includes('/auth/csrf-cookie')) {
      const http: HttpClient = this.http
      of(this.delayDueToRateLimitUntil).pipe(
        delay(this.delayDueToRateLimitUntil),
        switchMap(() => http.get('/auth/csrf-cookie', {observe: 'response'})),
        switchMap(() => {

          return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {

              if (error.error instanceof ErrorEvent) {
                // client-side error
              } else {
                if (error.status === 401) {
                  // unauthenticated - save current URL for redirect after login
                  this.redirectUrlService.setRedirectUrl(this.router.url)
                  this.injector.get(UserSessionService).logout().subscribe()
                }
              }
              return throwError(error)
            })
          )
        })
      )
    }

    return of(this.delayDueToRateLimitUntil)
      .pipe(
        delay(this.delayDueToRateLimitUntil),
        switchMap(() => next.handle(req)),
        catchError((error: HttpErrorResponse) => {

          if (error.status === 401 && !req.url.includes('logout')) {
            // unauthenticated - save current URL for redirect after login
            this.redirectUrlService.setRedirectUrl(this.router.url)
            this.injector.get(UserSessionService).logout().subscribe();
          }
          if (error.status === 429) {
            const reset_ts = error.headers.get('x-ratelimit-reset');
            if (reset_ts) {
              this.delayDueToRateLimitUntil = fromUnixTime(parseInt(reset_ts, 10));
              this.toastr.error('Rate limited! Please retry in: ' + formatDistanceToNow(this.delayDueToRateLimitUntil, {includeSeconds: true}));
            }
          }
          if ((error.status === 400 || error.status === 423) && error.error?.data?.error?.message) {
            this.toastr.error(error.error.data.error.message)
          }

          this.handleIdempotencyErrors(error);

          throw error;
        })
      )
  }

  private handleIdempotencyErrors(error: HttpErrorResponse): void {
    const errorType = error.error?.data?.error?.type;

    if (!errorType) {
      return;
    }

    if (errorType === PublicErrorCodeEnum.IDEMPOTENCY_CONFLICT_EXCEPTION) {
      const message = error.error?.data?.error?.message || this.translateService.instant('errors.idempotency.conflict');
      this.toastr.error(message, this.translateService.instant('errors.idempotency.conflict_title'));
    }

    if (errorType === PublicErrorCodeEnum.IDEMPOTENCY_KEY_REQUIRED_EXCEPTION) {
      const message = error.error?.data?.error?.message || this.translateService.instant('errors.idempotency.key_required');
      this.toastr.error(message, this.translateService.instant('errors.idempotency.key_required_title'));
    }
  }

  public constructor(public injector: Injector) {
    this.http = injector.get(HttpClient)
    this.localeService = injector.get(LocaleService)
    this.redirectUrlService = injector.get(RedirectUrlService)
    this.router = injector.get(Router)
    this.toastr = injector.get(ToastrService)
    this.translateService = injector.get(TranslateService)
  }
}
